import React from 'react';

interface JobMatchLogoProps {
  variant?: 'full' | 'horizontal' | 'emblem' | 'compact';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  showTagline?: boolean;
}

export const JobMatchLogo: React.FC<JobMatchLogoProps> = ({
  variant = 'horizontal',
  size = 'md',
  className = '',
  showTagline = true,
}) => {
  // Pure vector emblem representing the uploaded official JobMatch.ng logo
  const renderEmblem = (emblemSizeClass: string) => (
    <svg
      viewBox="0 0 300 220"
      className={`${emblemSizeClass} shrink-0`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="logoBlueGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#005db9" />
          <stop offset="100%" stopColor="#00438c" />
        </linearGradient>
        <linearGradient id="logoGreenGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#008a44" />
          <stop offset="100%" stopColor="#006b34" />
        </linearGradient>
        <linearGradient id="logoYellowGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#ffb703" />
          <stop offset="100%" stopColor="#f59e0b" />
        </linearGradient>
      </defs>

      {/* House Silhouette Behind Handshake */}
      <path
        d="M 115 70 L 150 35 L 185 70 L 185 105 L 115 105 Z"
        fill="url(#logoGreenGrad)"
      />
      {/* 4-Pane White Window */}
      <rect x="140" y="55" width="8" height="8" rx="1.5" fill="#ffffff" />
      <rect x="152" y="55" width="8" height="8" rx="1.5" fill="#ffffff" />
      <rect x="140" y="67" width="8" height="8" rx="1.5" fill="#ffffff" />
      <rect x="152" y="67" width="8" height="8" rx="1.5" fill="#ffffff" />

      {/* Left 'J' Construction Hard Hat */}
      <path d="M 68 45 C 68 20 112 20 112 45 Z" fill="url(#logoYellowGrad)" />
      <path
        d="M 60 45 L 120 45 C 122 45 122 51 120 51 L 60 51 C 58 51 58 45 60 45 Z"
        fill="url(#logoYellowGrad)"
      />
      <rect x="86" y="24" width="4" height="21" fill="#ea580c" opacity="0.6" rx="1" />
      <rect x="94" y="24" width="4" height="21" fill="#ea580c" opacity="0.6" rx="1" />

      {/* 'J' Head Under Hard Hat */}
      <circle cx="90" cy="58" r="14" fill="url(#logoBlueGrad)" />

      {/* 'J' Curve Body */}
      <path
        d="M 80 72 L 100 72 L 100 120 C 100 152 56 158 35 136 C 22 122 26 102 38 94 C 48 88 59 98 56 109 C 53 117 64 123 74 117 C 78 114 78 108 78 95 L 78 72 Z"
        fill="url(#logoBlueGrad)"
      />

      {/* Right Artisan Figure */}
      <circle cx="210" cy="58" r="19" fill="url(#logoGreenGrad)" />
      <path
        d="M 178 98 C 178 78 242 78 242 98 L 242 155 C 242 160 220 160 220 148 L 220 115 L 178 115 Z"
        fill="url(#logoGreenGrad)"
      />

      {/* White Wrench Cutout on Artisan */}
      <path
        d="M 204 155 L 204 105 C 200 100 200 88 210 84 C 216 82 222 86 224 92 C 224 95 218 96 216 94 C 213 91 208 94 209 98 C 210 100 214 101 218 103 L 218 155 Z"
        fill="#ffffff"
      />

      {/* Handshake: Blue Arm Reaching Right */}
      <path d="M 100 100 L 146 132 L 134 148 L 100 120 Z" fill="url(#logoBlueGrad)" />
      {/* Handshake: Green Arm Reaching Left */}
      <path d="M 200 100 L 150 132 L 140 116 L 178 94 Z" fill="url(#logoGreenGrad)" />

      {/* Firm Handshake Fingers Grip */}
      <path d="M 140 120 L 160 136 L 152 148 L 132 132 Z" fill="url(#logoBlueGrad)" />
      <circle cx="128" cy="136" r="4" fill="#008a44" />
      <circle cx="135" cy="143" r="4" fill="#008a44" />
      <circle cx="142" cy="149" r="4" fill="#008a44" />
      <circle cx="149" cy="155" r="4" fill="#008a44" />
      <path
        d="M 136 118 C 148 120 158 128 162 134"
        stroke="#00438c"
        strokeWidth="3.5"
        strokeLinecap="round"
      />
    </svg>
  );

  // Size mapping
  const emblemSizeClasses = {
    sm: 'w-7 h-6',
    md: 'w-9 h-7 sm:w-10 sm:h-8',
    lg: 'w-14 h-11',
    xl: 'w-20 h-16',
  };

  const textTitleSizes = {
    sm: 'text-sm font-extrabold',
    md: 'text-base sm:text-lg font-extrabold',
    lg: 'text-xl sm:text-2xl font-black',
    xl: 'text-2xl sm:text-3xl font-black',
  };

  if (variant === 'emblem') {
    return (
      <div className={`inline-flex items-center justify-center ${className}`}>
        {renderEmblem(emblemSizeClasses[size])}
      </div>
    );
  }

  if (variant === 'full') {
    return (
      <div className={`flex flex-col items-center text-center space-y-3 ${className}`}>
        {/* Top Emblem */}
        <div className="w-28 h-22 sm:w-36 sm:h-28 flex items-center justify-center">
          {renderEmblem('w-28 h-22 sm:w-36 sm:h-28')}
        </div>

        {/* Brand Name Typography */}
        <div className="flex items-center justify-center">
          <span className="text-3xl sm:text-4xl font-black tracking-tight text-[#0057B7] dark:text-blue-400">
            Job
          </span>
          <span className="text-3xl sm:text-4xl font-black tracking-tight text-[#008751] dark:text-emerald-400">
            Match
          </span>
          <span className="text-3xl sm:text-4xl font-black tracking-tight text-slate-800 dark:text-slate-100">
            .ng
          </span>
        </div>

        {/* Tagline */}
        {showTagline && (
          <p className="text-xs sm:text-sm font-bold text-slate-700 dark:text-slate-300 tracking-wide">
            Post Your Job <span className="text-[#008751] dark:text-emerald-400 mx-1">•</span> Get Matched{' '}
            <span className="text-[#008751] dark:text-emerald-400 mx-1">•</span> Get It Done
          </p>
        )}

        {/* Trade Badges Strip */}
        <div className="pt-2 border-t border-slate-200/80 dark:border-slate-800 flex flex-wrap items-center justify-center gap-2 sm:gap-3 text-[10px] sm:text-xs font-semibold text-slate-600 dark:text-slate-300">
          <span className="flex items-center gap-1">
            <span className="text-blue-500">💧</span> Plumber
          </span>
          <span className="flex items-center gap-1">
            <span className="text-amber-500">🔨</span> Carpenter
          </span>
          <span className="flex items-center gap-1">
            <span className="text-rose-500">🧱</span> Bricklayer
          </span>
          <span className="flex items-center gap-1">
            <span className="text-purple-500">🖌️</span> Painter
          </span>
          <span className="flex items-center gap-1">
            <span className="text-emerald-500">⚡</span> Electrician
          </span>
          <span className="flex items-center gap-1">
            <span className="text-cyan-500">⏹️</span> Tiler
          </span>
          <span className="flex items-center gap-1">
            <span className="text-slate-500 dark:text-slate-400">🔧</span> & More
          </span>
        </div>
      </div>
    );
  }

  // Horizontal variant: Emblem on the left, typography cleanly aligned on the right
  if (variant === 'horizontal') {
    return (
      <div className={`inline-flex items-center gap-2.5 sm:gap-3 group text-left ${className}`}>
        {renderEmblem(`${emblemSizeClasses[size]} transition-transform duration-200 group-hover:scale-105`)}
        <div className="flex flex-col justify-center leading-none">
          <div className={`flex items-baseline tracking-tight ${textTitleSizes[size]}`}>
            <span className="text-[#0057B7] dark:text-blue-400 transition-colors">Job</span>
            <span className="text-[#008751] dark:text-emerald-400 transition-colors">Match</span>
            <span className="text-slate-800 dark:text-slate-200 transition-colors">.ng</span>
          </div>
          {showTagline && (
            <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 tracking-tight mt-0.5 hidden sm:inline-block">
              Verified Nigerian Marketplace
            </span>
          )}
        </div>
      </div>
    );
  }

  // Compact variant: Sleek inline single-line lockup for dense spaces (modals, compact bars)
  return (
    <div className={`inline-flex items-center gap-2 text-left ${className}`}>
      {renderEmblem(`${emblemSizeClasses[size]} shrink-0`)}
      <div className="flex items-baseline tracking-tight font-extrabold text-sm sm:text-base leading-none">
        <span className="text-[#0057B7] dark:text-blue-400">Job</span>
        <span className="text-[#008751] dark:text-emerald-400">Match</span>
        <span className="text-slate-800 dark:text-slate-200">.ng</span>
      </div>
    </div>
  );
};
